import * as Actions from './actions'
import * as Request from '../../request'
import * as Common from '../common'

export interface SurveyResultsState {
  survey: Actions.Survey | null;
  options: Actions.SurveyOption[];
  configurations: Common.KeyValue[];
  isLoading: boolean;
  errored: boolean;
  errorMessage: string;
  surveyId: string;
  identifier: string;
  guid: string;
  surveyOptionId: string;
  token: string;
  totalResponses: number;
  // Feedback information from store API response
  feedbackMessage: string;
  isAlreadySubmitted: boolean;
  isCorrect: boolean;
  hasFeedback: boolean;
  // Track if store call has been initiated for current token+option
  storeCallInitiated: boolean;
}

export interface WrappedSurveyResults {
  surveyResults: SurveyResultsState;
}

const initialState: SurveyResultsState = {
  survey: null,
  options: [],
  configurations: [],
  isLoading: false,
  errored: false,
  errorMessage: '',
  surveyId: '',
  identifier: '',
  guid: '',
  surveyOptionId: '',
  token: '',
  totalResponses: 0,
  // Feedback state
  feedbackMessage: '',
  isAlreadySubmitted: false,
  isCorrect: false,
  hasFeedback: false,
  storeCallInitiated: false,
};

export const surveyResultsReducer = (
  state = initialState,
  action: any
): SurveyResultsState => {
  
  switch (action.type) {
    case Actions.INITIALIZE_SURVEY_RESULTS_FROM_URL:
      // Reset storeCallInitiated if token or surveyOptionId changed
      const tokenChanged = state.token !== (action.payload.token || '');
      const optionChanged = state.surveyOptionId !== (action.payload.surveyOptionId || '');
      
      return {
        ...state,
        surveyId: action.payload.surveyId,
        identifier: action.payload.communityIdentifier,
        guid: action.payload.guid || '',
        surveyOptionId: action.payload.surveyOptionId || '',
        token: action.payload.token || '',
        // Reset store call flag if token or option changed
        storeCallInitiated: (tokenChanged || optionChanged) ? false : state.storeCallInitiated,
        // Preserve existing survey data, configurations, and other state
        survey: state.survey,
        options: state.options,
        configurations: state.configurations,
        totalResponses: state.totalResponses,
        // Reset feedback state if token or option changed
        feedbackMessage: (tokenChanged || optionChanged) ? '' : state.feedbackMessage,
        isAlreadySubmitted: (tokenChanged || optionChanged) ? false : state.isAlreadySubmitted,
        isCorrect: (tokenChanged || optionChanged) ? false : state.isCorrect,
        hasFeedback: (tokenChanged || optionChanged) ? false : state.hasFeedback,
      };

    case Actions.FETCH_SURVEY_RESULTS:
      return {
        ...state,
        isLoading: true,
        errored: false,
        errorMessage: '',
      };

    case Request.successConstant(Actions.FETCH_SURVEY_RESULTS):
      return {
        ...state,
        isLoading: false,
        errored: false,
        errorMessage: state.isAlreadySubmitted ? state.errorMessage : '', // Preserve error message if already submitted
        survey: action.payload.response.survey,
        options: action.payload.response.results || [], // API returns 'results' array
        totalResponses: action.payload.response.total_responses || 0,
        // Preserve configurations - don't reset them
        configurations: state.configurations,
        // Preserve feedback state - don't reset it
        feedbackMessage: state.feedbackMessage,
        isAlreadySubmitted: state.isAlreadySubmitted,
        isCorrect: state.isCorrect,
        hasFeedback: state.hasFeedback,
      };

    case Request.successConstant(Common.FETCH_CONFIGURATION):
      return {
        ...state,
        ...Common.configurationsState(action.payload.response),
      };

    case Request.errorConstant(Actions.FETCH_SURVEY_RESULTS):
      return {
        ...state,
        isLoading: false,
        errored: true,
        errorMessage: 'Failed to load survey results',
        survey: null,
        options: [],
      };

    case Actions.STORE_SURVEY_RESULT:
      return {
        ...state,
        isLoading: true,
        errored: false,
        errorMessage: '',
        storeCallInitiated: true,
      };

    case Request.successConstant(Actions.STORE_SURVEY_RESULT):
      const response = action.payload.response;
      return {
        ...state,
        isLoading: false,
        errored: false,
        errorMessage: '',
        // Set feedback from API response
        feedbackMessage: response.message,
        isAlreadySubmitted: response.is_already_submitted,
        isCorrect: response.is_correct,
        hasFeedback: true,
      };

    case Request.errorConstant(Actions.STORE_SURVEY_RESULT):
      return {
        ...state,
        isLoading: false,
        errored: true,
        errorMessage: 'Failed to store survey result',
      };

    default:
      return state;
  }
};